import {
  INITIAL_ARTICLE_STATE,
  NEXT_ARTICLE_STATE,
} from "./scenarios";
import type {
  ArticleState,
  MutationConfiguration,
  StateScenario,
  StateSimulationResult,
  TimelineStep,
} from "./types";

function copyState(state: ArticleState): ArticleState {
  return { ...state };
}

function articleBody(state: ArticleState): string {
  return JSON.stringify({
    id: 123,
    status: state.status,
    title: state.title,
  });
}

function projectionRead(state: ArticleState, includeCapability: boolean): string {
  const profile = includeCapability
    ? "\nLink: <https://www.ietf.org/archive/id/draft-jurkovikj-httpapi-agentic-state-02.html#ast-semantic-profile>; rel=\"profile\""
    : "";
  return [
    "GET /article/123 HTTP/1.1",
    "Host: example.com",
    "Accept: text/html",
    "",
    "HTTP/1.1 200 OK",
    "Content-Type: text/html",
    `ETag: ${state.projectionEtag}`,
    `Semantic-ETag: ${state.semanticEtag}`,
    'Link: </api/article/123>; rel="concurrency-state"; type="application/json"' +
      profile,
  ].join("\n");
}

function sbrRead(state: ArticleState): string {
  return [
    "GET /api/article/123 HTTP/1.1",
    "Host: example.com",
    "Accept: application/json",
    "Accept-Encoding: identity",
    "",
    "HTTP/1.1 200 OK",
    "Content-Type: application/json",
    `ETag: ${state.sbrEtag}`,
    `Semantic-ETag: ${state.semanticEtag}`,
    "",
    articleBody(state),
  ].join("\n");
}

function mutationRequest(configuration: MutationConfiguration): string {
  const body = '{"status":"draft"}';
  const target =
    configuration.target === "sbr" ? "/api/article/123" : "/article/123";
  const lines = [
    `PATCH ${target} HTTP/1.1`,
    "Host: example.com",
    "Content-Type: application/merge-patch+json",
    `Content-Length: ${body.length}`,
  ];
  if (configuration.precondition !== undefined) {
    lines.push(
      `${configuration.precondition.field}: ${configuration.precondition.value}`,
    );
  }
  lines.push("", body);
  return lines.join("\n");
}

function semanticCapabilityContract(): string {
  return [
    "Capability source: application contract",
    "Target: /article/123",
    "Method: PATCH",
    "Profile: AST Semantic -02",
    "Enforcement: If-Semantic-Match required",
  ].join("\n");
}

function observationResult(scenario: StateScenario): StateSimulationResult {
  const state = copyState(INITIAL_ARTICLE_STATE);
  const steps: readonly TimelineStep[] = [
    {
      number: "01",
      eyebrow: "Projected read",
      title: "Observe the HTML representation",
      detail:
        "The ordinary ETag belongs to these HTML bytes. The semantic validator identifies the logical article state.",
      wire: projectionRead(state, true),
      tone: "neutral",
    },
    {
      number: "02",
      eyebrow: "State-bearing read",
      title: "Observe the JSON SBR",
      detail:
        "The JSON bytes have another ordinary ETag, while the equal Semantic-ETag binds the resources to one declared semantic domain.",
      wire: sbrRead(state),
      tone: "neutral",
    },
    {
      number: "03",
      eyebrow: "Scope check",
      title: "Keep the validators in their lanes",
      detail:
        "Use each ETag only for its selected representation. Semantic-ETag does not replace ETag, create a cache key, or authorize 304.",
      wire: [
        `HTML ETag       ${state.projectionEtag}`,
        `JSON ETag       ${state.sbrEtag}`,
        `Logical state   ${state.semanticEtag}`,
        "Cache result    ordinary HTTP validators only",
      ].join("\n"),
      tone: "success",
    },
  ];

  return {
    scenarioId: scenario.id,
    modeLabel: "Read-only validator comparison",
    outcome: "observed",
    outcomeLabel: "Scopes kept distinct",
    httpStatus: 200,
    explanation:
      "The same logical state can have several byte representations. Ordinary ETags remain representation-scoped; the semantic validator is a separate origin-server coordination signal.",
    stateBefore: copyState(state),
    stateAtCompare: copyState(state),
    stateAfter: copyState(state),
    clientCommitCount: 0,
    externalCommitCount: 0,
    requestCount: 2,
    steps,
  };
}

function capabilityBlockedResult(
  scenario: StateScenario,
  configuration: MutationConfiguration,
): StateSimulationResult {
  const state = copyState(INITIAL_ARTICLE_STATE);
  return {
    scenarioId: scenario.id,
    modeLabel: "AST Semantic",
    outcome: "client_blocked",
    outcomeLabel: "Request not sent",
    httpStatus: null,
    explanation:
      "Semantic-ETag can be observational metadata. Without positive target-and-method-specific enforcement knowledge, the client cannot treat If-Semantic-Match as a mutation-safety condition.",
    stateBefore: copyState(state),
    stateAtCompare: copyState(state),
    stateAfter: copyState(state),
    clientCommitCount: 0,
    externalCommitCount: 0,
    requestCount: 1,
    steps: [
      {
        number: "01",
        eyebrow: "Observation",
        title: "A semantic validator is present",
        detail:
          "The response identifies logical state, but it contains no positive AST Semantic enforcement signal for PATCH.",
        wire: projectionRead(state, configuration.capabilityKnown),
        tone: "neutral",
      },
      {
        number: "02",
        eyebrow: "Client safety gate",
        title: "Do not rely on an unknown extension",
        detail:
          "An unaware server could ignore If-Semantic-Match. The safe client stops before constructing an unsafe request.",
        wire: [
          "Capability for PATCH /article/123: unknown",
          "Unsafe request: not sent",
          "State transition: none",
        ].join("\n"),
        tone: "warning",
      },
    ],
  };
}

function projectedCoreRejection(
  scenario: StateScenario,
  configuration: MutationConfiguration,
): StateSimulationResult {
  const state = copyState(INITIAL_ARTICLE_STATE);
  return {
    scenarioId: scenario.id,
    modeLabel: "AST Core",
    outcome: "rejected",
    outcomeLabel: "405 · Wrong target",
    httpStatus: 405,
    explanation:
      "The projected resource does not reinterpret the JSON SBR's ETag. It rejects the unsafe method and points the client to the State-Bearing Resource.",
    stateBefore: copyState(state),
    stateAtCompare: copyState(state),
    stateAfter: copyState(state),
    clientCommitCount: 0,
    externalCommitCount: 0,
    requestCount: 2,
    steps: [
      {
        number: "01",
        eyebrow: "SBR observation",
        title: "Obtain the JSON representation validator",
        detail: "This ETag is scoped to the selected SBR at /api/article/123.",
        wire: sbrRead(state),
        tone: "neutral",
      },
      {
        number: "02",
        eyebrow: "Invalid transfer",
        title: "Send that ETag to another target",
        detail:
          "Changing the request target does not broaden the meaning of ordinary If-Match.",
        wire: mutationRequest(configuration),
        tone: "warning",
      },
      {
        number: "03",
        eyebrow: "Origin response",
        title: "Reject and identify the SBR",
        detail: "No comparison is repurposed and no state transition occurs.",
        wire: [
          "HTTP/1.1 405 Method Not Allowed",
          "Allow: GET, HEAD",
          'Link: </api/article/123>; rel="concurrency-state"; type="application/json"',
        ].join("\n"),
        tone: "rejected",
      },
    ],
  };
}

function missingPreconditionResult(
  scenario: StateScenario,
  configuration: MutationConfiguration,
): StateSimulationResult {
  const state = copyState(INITIAL_ARTICLE_STATE);
  const field = configuration.mode === "core" ? "If-Match" : "If-Semantic-Match";
  return {
    scenarioId: scenario.id,
    modeLabel: configuration.mode === "core" ? "AST Core" : "AST Semantic",
    outcome: "rejected",
    outcomeLabel: "428 · Precondition required",
    httpStatus: 428,
    explanation:
      `The protected mutation omitted its required ${field} condition. The origin fails before applying the transition.`,
    stateBefore: copyState(state),
    stateAtCompare: copyState(state),
    stateAfter: copyState(state),
    clientCommitCount: 0,
    externalCommitCount: 0,
    requestCount: 2,
    steps: [
      {
        number: "01",
        eyebrow: "Current state",
        title: "The SBR exposes an observed version",
        detail: "A protected update must carry a version-specific condition.",
        wire: sbrRead(state),
        tone: "neutral",
      },
      {
        number: "02",
        eyebrow: "Unsafe request",
        title: `Omit ${field}`,
        detail:
          "Normal request checks complete, but the profile's required concurrency condition is absent.",
        wire: mutationRequest(configuration),
        tone: "warning",
      },
      {
        number: "03",
        eyebrow: "Origin response",
        title: "Require a precondition",
        detail: "The request is rejected and the state remains revision 7.",
        wire: [
          "HTTP/1.1 428 Precondition Required",
          "Content-Type: application/problem+json",
          "",
          JSON.stringify({
            type: "https://example.com/problems/precondition-required",
            title: "Precondition required",
            status: 428,
            detail: `${field} is required for this mutation.`,
          }),
        ].join("\n"),
        tone: "rejected",
      },
    ],
  };
}

function evaluatedMutationResult(
  scenario: StateScenario,
  configuration: MutationConfiguration,
): StateSimulationResult {
  const before = copyState(INITIAL_ARTICLE_STATE);
  const current = copyState(configuration.interveningState ?? before);
  const requiredField =
    configuration.mode === "core" ? "If-Match" : "If-Semantic-Match";
  const expected =
    configuration.mode === "core" ? current.sbrEtag : current.semanticEtag;
  const supplied = configuration.precondition;

  if (supplied === undefined || supplied.field !== requiredField) {
    return missingPreconditionResult(scenario, configuration);
  }

  const readWire =
    configuration.mode === "core"
      ? sbrRead(before)
      : projectionRead(before, configuration.capabilityKnown);
  const steps: TimelineStep[] = [
    {
      number: "01",
      eyebrow: "Observe",
      title:
        configuration.mode === "core"
          ? "Read the State-Bearing Representation"
          : "Read a participating projection",
      detail:
        configuration.mode === "core"
          ? "The client retains the strong ETag of the SBR selected at the mutation target."
          : "The client retains the semantic validator after positively identifying AST Semantic support for this resource and method.",
      wire: readWire,
      tone: "neutral",
    },
  ];

  if (configuration.mode === "semantic") {
    steps.push({
      number: "02",
      eyebrow: "Capability contract",
      title: "Bind support to this target and method",
      detail:
        "The profile link identifies AST Semantic participation. Separate application documentation establishes that PATCH on this resource enforces If-Semantic-Match.",
      wire: semanticCapabilityContract(),
      tone: "success",
    });
  }

  if (configuration.interveningState !== undefined) {
    steps.push({
      number: String(steps.length + 1).padStart(2, "0"),
      eyebrow: "Intervening writer",
      title: "The concurrency domain advances",
      detail:
        "Another authorized writer commits before this client's condition is evaluated.",
      wire: [
        "Atomic external commit",
        `Previous semantic state: ${before.semanticEtag}`,
        `Current semantic state:  ${current.semanticEtag}`,
      ].join("\n"),
      tone: "warning",
    });
  }

  steps.push({
    number: String(steps.length + 1).padStart(2, "0"),
    eyebrow: "Conditional request",
    title: `Present ${requiredField}`,
    detail:
      "The request carries the version against which the intended mutation was constructed.",
    wire: mutationRequest(configuration),
    tone: "neutral",
  });

  if (supplied.value !== expected) {
    steps.push({
      number: String(steps.length + 1).padStart(2, "0"),
      eyebrow: "Atomic comparison",
      title: "The supplied validator is stale",
      detail:
        "The false condition rejects the operation. The returned current validator is advisory and cannot be substituted blindly.",
      wire: [
        "HTTP/1.1 412 Precondition Failed",
        "Content-Type: application/problem+json",
        `Semantic-ETag: ${current.semanticEtag}`,
        'Link: </api/article/123>; rel="concurrency-state"; type="application/json"',
      ].join("\n"),
      tone: "rejected",
    });

    return {
      scenarioId: scenario.id,
      modeLabel:
        configuration.mode === "core" ? "AST Core" : "AST Semantic",
      outcome: "rejected",
      outcomeLabel: "412 · Precondition failed",
      httpStatus: 412,
      explanation:
        "The current logical state no longer matches the state the client observed. The client must retrieve or validate current state and reconcile its intent before trying again.",
      stateBefore: before,
      stateAtCompare: current,
      stateAfter: copyState(current),
      clientCommitCount: 0,
      externalCommitCount:
        configuration.interveningState === undefined ? 0 : 1,
      requestCount: 2,
      steps,
    };
  }

  const after = copyState(NEXT_ARTICLE_STATE);
  const responseValidator =
    configuration.mode === "core"
      ? `ETag: ${after.sbrEtag}`
      : `Semantic-ETag: ${after.semanticEtag}`;
  steps.push({
    number: String(steps.length + 1).padStart(2, "0"),
    eyebrow: "Atomic commit",
    title: "Compare, transition, and publish",
    detail:
      "The condition is true. The state transition and resulting validator become visible as one operation.",
    wire: ["HTTP/1.1 204 No Content", responseValidator].join("\n"),
    tone: "success",
  });

  return {
    scenarioId: scenario.id,
    modeLabel: configuration.mode === "core" ? "AST Core" : "AST Semantic",
    outcome: "committed",
    outcomeLabel: "204 · State committed",
    httpStatus: 204,
    explanation:
      "The version-specific precondition matched the current concurrency domain, so the origin committed exactly one transition and published the resulting validator.",
    stateBefore: before,
    stateAtCompare: current,
    stateAfter: after,
    clientCommitCount: 1,
    externalCommitCount: 0,
    requestCount: 2,
    steps,
  };
}

function concurrentResult(scenario: StateScenario): StateSimulationResult {
  const before = copyState(INITIAL_ARTICLE_STATE);
  const after = copyState(NEXT_ARTICLE_STATE);
  const request = [
    "PATCH /api/article/123 HTTP/1.1",
    "Host: example.com",
    `If-Match: ${before.sbrEtag}`,
    "Content-Type: application/merge-patch+json",
    "Content-Length: 18",
    "",
    '{"status":"draft"}',
  ].join("\n");

  return {
    scenarioId: scenario.id,
    modeLabel: "AST Core · two writers",
    outcome: "guarded_concurrency",
    outcomeLabel: "1 commit · 1 rejection",
    httpStatus: 412,
    explanation:
      "Writer A atomically advances the SBR to revision 8. Writer B's later comparison against the same old ETag is false, so it cannot overwrite A's transition.",
    stateBefore: before,
    stateAtCompare: after,
    stateAfter: after,
    clientCommitCount: 1,
    externalCommitCount: 0,
    requestCount: 3,
    steps: [
      {
        number: "01",
        eyebrow: "Shared observation",
        title: "Both writers see revision 7",
        detail:
          "Each client constructs an intended mutation against the same selected SBR validator.",
        wire: sbrRead(before),
        tone: "neutral",
      },
      {
        number: "02",
        eyebrow: "Writer A",
        title: "The first comparison commits",
        detail:
          "The current ETag matches. The origin publishes revision 8 and json-v19 atomically.",
        wire: `${request}\n\nHTTP/1.1 204 No Content\nETag: ${after.sbrEtag}`,
        tone: "success",
      },
      {
        number: "03",
        eyebrow: "Writer B",
        title: "The old condition no longer matches",
        detail:
          "The second writer cannot pass against the old validator after Writer A commits.",
        wire: `${request}\n\nHTTP/1.1 412 Precondition Failed\nETag: ${after.sbrEtag}`,
        tone: "rejected",
      },
      {
        number: "04",
        eyebrow: "Invariant",
        title: "Exactly one state transition is visible",
        detail:
          "Every writer in the concurrency domain must participate in the same atomic boundary.",
        wire: [
          "Requests evaluated: 2",
          "Committed transitions: 1",
          "Rejected stale writes: 1",
          `Current SBR ETag: ${after.sbrEtag}`,
        ].join("\n"),
        tone: "success",
      },
    ],
  };
}

export function simulateStateScenario(
  scenario: StateScenario,
): StateSimulationResult {
  if (scenario.kind === "observe") {
    return observationResult(scenario);
  }
  if (scenario.kind === "concurrent") {
    return concurrentResult(scenario);
  }

  const configuration = scenario.mutation;
  if (configuration === undefined) {
    throw new Error(`Mutation scenario has no configuration: ${scenario.id}`);
  }
  if (configuration.mode === "semantic" && !configuration.capabilityKnown) {
    return capabilityBlockedResult(scenario, configuration);
  }
  if (configuration.mode === "core" && configuration.target === "projection") {
    return projectedCoreRejection(scenario, configuration);
  }
  return evaluatedMutationResult(scenario, configuration);
}
